<script setup lang="ts">
import { reactive, watch } from "vue";
import {
  TuButton,
  TuInputLabel,
  TuInputPassword,
  PasswordStrengthMeasure,
  useStrengthMeasure,
  type FieldValidationRule,
  type PasswordStrengthLabels,
} from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import useTranslation from "@Composables/useTranslation";

//
// Setup
const { __ } = useTranslation();
const { passwordStrengthMeasure, updateStrengthMeasure } = useStrengthMeasure();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Props
interface Props {
  updatePassword: (params: {
    currentPassword: string;
    password: string;
    passwordConfirmation: string;
  }) => Promise<boolean>;
  errors?: Record<string, string>;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => ({}),
  loading: false,
});

//
// Data
type FormField = "currentPassword" | "password" | "passwordConfirmation";

const form = reactive<Record<FormField, string>>({
  currentPassword: "",
  password: "",
  passwordConfirmation: "",
});

const localErrors = reactive<Record<FormField, string | null>>({
  currentPassword: null,
  password: null,
  passwordConfirmation: null,
});

const meterLabels: PasswordStrengthLabels = {
  tooltipLabel: __("password_strength_labels.tooltip_label"),
  tooltipContent: __("password_strength_labels.tooltip_content"),
  measureDefaultLabel: __("password_strength_labels.default"),
  measureWeakLabel: __("password_strength_labels.weak"),
  measureMediumLabel: __("password_strength_labels.medium"),
  measureStrongLabel: __("password_strength_labels.strong"),
};

const currentPasswordValidations: FieldValidationRule[] = [
  {
    validator: (value: string) => value !== "",
    errorMessage: __("security_settings.update_password_form.current_password_required_error"),
  },
];

const passwordValidations: FieldValidationRule[] = [
  {
    validator: (value: string) => value !== "",
    errorMessage: __("security_settings.update_password_form.new_password_required_error"),
  },
  {
    validator: () => passwordStrengthMeasure.value !== PasswordStrengthMeasure.WEAK,
    errorMessage: __("security_settings.update_password_form.password_too_weak_error"),
  },
];

const passwordConfirmationValidations: FieldValidationRule[] = [
  {
    validator: (value: string) => value !== "",
    errorMessage: __("security_settings.update_password_form.confirm_password_required_error"),
  },
  {
    validator: (value: string) => value === form.password,
    errorMessage: __("security_settings.update_password_form.passwords_do_not_match_error"),
  },
];

//
// Methods
function cleanUpError(field: FormField): void {
  localErrors[field] = null;
}

function isFieldValid(field: FormField, rules: FieldValidationRule[]): boolean {
  return rules.every((rule) => {
    const isValid = rule.validator(form[field]);

    if (!isValid) {
      localErrors[field] = typeof rule.errorMessage === "string"
        ? rule.errorMessage
        : rule.errorMessage();
    }

    return isValid;
  });
}

function onPasswordInput(password: string): void {
  form.password = password;

  updateStrengthMeasure(password);
}

function resetForm(): void {
  form.currentPassword = "";
  form.password = "";
  form.passwordConfirmation = "";

  updateStrengthMeasure("");
}

async function onSubmit(): Promise<void> {
  if (props.loading) {
    return;
  }

  const validations = [
    isFieldValid("currentPassword", currentPasswordValidations),
    isFieldValid("password", passwordValidations),
    isFieldValid("passwordConfirmation", passwordConfirmationValidations),
  ];

  if (!validations.every(Boolean)) {
    return;
  }

  const isUpdated = await props.updatePassword({
    currentPassword: form.currentPassword,
    password: form.password,
    passwordConfirmation: form.passwordConfirmation,
  });

  if (isUpdated) {
    resetForm();
  }
}

//
// Watchers
watch(() => props.errors, (givenErrors) => {
  localErrors.currentPassword = givenErrors.current_password ?? null;
  localErrors.password = givenErrors.password ?? null;
  localErrors.passwordConfirmation = givenErrors.password_confirmation ?? null;
});

//
// Exposed Test Vars
defineExpose({ form, localErrors });
</script>

<template>
  <form
    class="update-password-form"
    :class="{ 'update-password-form--mobile': isMobileView }"
    @submit.prevent="onSubmit"
  >
    <div class="update-password-form__input">
      <TuInputLabel
        id="current_password"
        class="update-password-form__label"
        :label="__('security_settings.update_password_form.current_password_label')"
      />

      <TuInputPassword
        class="_current_password"
        :label="{ id: 'current_password', label: '' }"
        :input="{ placeholder: __('security_settings.update_password_form.current_password_placeholder') }"
        :value="form.currentPassword"
        :copy="{ ...(localErrors.currentPassword && { errorMessage: localErrors.currentPassword }) }"
        @input="form.currentPassword = $event"
        @focus="cleanUpError('currentPassword')"
        @focusout="cleanUpError('currentPassword')"
      />
    </div>

    <div class="update-password-form__input">
      <TuInputLabel
        id="new_password"
        class="update-password-form__label"
        :label="__('security_settings.update_password_form.new_password_label')"
      />

      <TuInputPassword
        class="_new_password"
        :label="{ id: 'new_password', label: '' }"
        :input="{ placeholder: __('security_settings.update_password_form.new_password_placeholder') }"
        :value="form.password"
        :copy="{ ...(localErrors.password && { errorMessage: localErrors.password }) }"
        :password-meter="{
          passwordStrengthMeasure,
          passwordStrengthLabels: meterLabels,
        }"
        @input="onPasswordInput"
        @focus="cleanUpError('password')"
        @focusout="cleanUpError('password')"
      />
    </div>

    <div class="update-password-form__input">
      <TuInputLabel
        id="confirm_password"
        class="update-password-form__label"
        :label="__('security_settings.update_password_form.confirm_password_label')"
      />

      <TuInputPassword
        class="_confirm_password"
        :label="{ id: 'confirm_password', label: '' }"
        :input="{ placeholder: __('security_settings.update_password_form.confirm_password_placeholder') }"
        :value="form.passwordConfirmation"
        :copy="{ ...(localErrors.passwordConfirmation && { errorMessage: localErrors.passwordConfirmation }) }"
        @input="form.passwordConfirmation = $event"
        @focus="cleanUpError('passwordConfirmation')"
        @focusout="cleanUpError('passwordConfirmation')"
      />
    </div>

    <div class="update-password-form__input">
      <div class="update-password-form__submission">
        <TuButton
          class="_submit"
          :label="__('security_settings.update_password_form.submit')"
          :is-disabled="loading"
          :is-block="isMobileView"
        />
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.update-password-form {
  $self: &;

  display: flex;
  flex-flow: column;
  gap: spacing.$xxs;
  padding: spacing.$xxs;

  background-color: color.$background-secondary;
  border-radius: border.$medium-radius;
  border: border.$primary;

  &__input {
    display: grid;
    grid-template-areas: "side main";
    grid-template-columns: 1fr 3fr;
  }

  &__label {
    align-items: flex-start;
  }

  &__submission {
    grid-area: main;
  }

  /**
   * The label beside the input replaces the one TuInputPassword renders above it.
   */
  :deep(.tu-input-password__label) {
    display: none;
  }

  :deep(.tu-input-password__input-wrapper) {
    margin-top: 0;
  }

  &--mobile {
    #{ $self }__input {
      display: flex;
      flex-flow: column;
      gap: spacing.$xxxs;
    }
  }
}
</style>
